import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const RegisterPage = () => {
  const defaultLogoUrl = 'https://customer-assets.emergentagent.com/job_35efb418-d957-4303-979f-4e5863096b08/artifacts/hzi2b2xm_amino-chain-logo-final-1.png';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(true);
  const [logoUrl, setLogoUrl] = useState(defaultLogoUrl);
  const [siteName, setSiteName] = useState('AMINO-CHAIN');
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadRegistrationSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/site`);
        if (response.ok) {
          const data = await response.json();
          setRequiresVerification(data.require_email_verification_for_registration !== false);
          if (data.logo_url) {
            setLogoUrl(data.logo_url);
          }
          if (data.site_name) {
            setSiteName(data.site_name);
          }
        }
      } catch (error) {
        setRequiresVerification(true);
      }
    };

    loadRegistrationSettings();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    const result = await register(formData.email, formData.name, formData.password);
    setIsLoading(false);

    if (result.success) {
      if (result.requiresVerification) {
        // Redirect to verification page
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}&sent=${result.emailSent}`);
      } else {
        toast({
          title: 'Welcome to AMINO-CHAIN!',
          description: 'Your account has been created.',
        });
        navigate('/premium-peptides');
      }
    } else {
      toast({
        title: 'Registration Failed',
        description: result.error,
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-600 via-purple-700 to-indigo-800 flex items-center justify-center p-6" data-testid="register-page">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl" />
      </div>

      {/* Top-left brand logo wired like homepage */}
      <Link
        to="/"
        className="absolute top-6 left-6 z-20"
        data-testid="register-top-left-logo-link"
      >
        <img
          src={logoUrl}
          alt={siteName}
          className="h-11 sm:h-12 w-auto object-contain"
          data-testid="register-top-left-logo-image"
        />
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center group" data-testid="register-brand-logo-link">
            <img
              src={logoUrl}
              alt={siteName}
              className="h-16 sm:h-20 w-auto object-contain group-hover:drop-shadow-[0_0_18px_rgba(255,255,255,0.35)] transition-all duration-300"
              data-testid="register-brand-logo-image"
            />
          </Link>
        </div>

        {/* Register card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <h1 className="font-heading text-2xl font-bold text-white mb-2">Create Account</h1>
            <p className="text-purple-200">
              Register to view product prices and place orders
              {requiresVerification ? ' (email verification code required)' : ''}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name field */}
            <div>
              <label className="block text-sm text-purple-200 mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  placeholder="John Doe"
                  data-testid="register-name"
                />
              </div>
            </div>

            {/* Email field */}
            <div>
              <label className="block text-sm text-purple-200 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  placeholder="you@example.com"
                  data-testid="register-email"
                />
              </div>
            </div>

            {/* Password field */}
            <div>
              <label className="block text-sm text-purple-200 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-12 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  placeholder="••••••••"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-purple-300 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password field */}
            <div>
              <label className="block text-sm text-purple-200 mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                  placeholder="••••••••"
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 py-4 bg-white text-purple-700 font-heading font-bold uppercase tracking-wider rounded-xl hover:bg-[#f4e4bc] hover:text-[#5b2a93] transition-all disabled:opacity-50"
              data-testid="register-submit"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-700 rounded-full animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Login link */}
          <div className="mt-6 text-center">
            <p className="text-purple-200">
              Already have an account?{' '}
              <Link to="/login" className="text-cyan-300 hover:text-white font-semibold transition-colors">
                Sign In
              </Link>
            </p>
          </div>
        </div>

        {/* Research notice */}
        <p className="mt-6 text-center text-sm text-purple-200/60">
          By registering, you confirm these products are for research purposes only.
        </p>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
